import { type FC } from 'react';

import {
  NotClearedTooltip,
  type NotClearedTooltipProps,
} from '@/src/components/atoms/NotClearedTooltip';
import { isLearnerNotClearedTooltipDisabled } from '@/src/components/atoms/NotClearedTooltip/util';
import { useSelectedRole } from '@/src/features/auth/hooks';
import { useHasFeatureFlag } from '@/src/hooks/featureFlags/useHasFeatureFlag';

interface LearnerNotClearedTooltipProps extends Omit<NotClearedTooltipProps, 'title'> {
  /**
   * The learner's cleared status. The tooltip is shown only when this is explicitly `false`; a
   * cleared learner (`true`) or an unknown status (`null`/`undefined`) disables it.
   *
   * Centralizing the polarity here keeps every consumer consistent; see
   * {@link isLearnerNotClearedTooltipDisabled}.
   */
  readonly cleared?: boolean | null;
  readonly learnerFirstName?: string;
}

export const LearnerNotClearedTooltip: FC<LearnerNotClearedTooltipProps> = ({
  children,
  cleared,
  learnerFirstName,
  ...props
}) => {
  const { organizationName } = useSelectedRole();

  const clearedFeatureEnabled = useHasFeatureFlag.ClearedStatusUx();

  return (
    <NotClearedTooltip
      {...props}
      isDisabled={
        Boolean(props.isDisabled) ||
        isLearnerNotClearedTooltipDisabled(cleared) ||
        !clearedFeatureEnabled
      }
      title={
        learnerFirstName
          ? `${learnerFirstName}'s account is not cleared for this program. ` +
            `Please review ${organizationName}'s clearance requirements.`
          : undefined
      }
    >
      {children}
    </NotClearedTooltip>
  );
};
